import dayjs, { type Dayjs, type OpUnitType } from 'dayjs'
import type { Duration } from 'dayjs/plugin/duration'
import { isInternalLanguage, type TInternalLanguage } from '@/utils/constants/locale'

// Pass only Riga timezone date
export function getRemainingRigaTzTime(startDate: Dayjs, endDate: Dayjs): Duration {
  const diff = endDate.diff(startDate)

  return getRemainingTime(diff)
}

function getRemainingTime(timeDifference: number): Duration {
  const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24))
  const remainderAfterDays = timeDifference - days * 1000 * 60 * 60 * 24

  const hours = Math.floor(remainderAfterDays / (1000 * 60 * 60))
  const remainderAfterHours = remainderAfterDays - hours * 1000 * 60 * 60

  const minutes = Math.floor(remainderAfterHours / (1000 * 60))
  const remainderAfterMinutes = remainderAfterHours - minutes * 1000 * 60

  const seconds = Math.floor(remainderAfterMinutes / 1000)

  return dayjs.duration({
    days: days < 0 ? 0 : days,
    hours: hours < 0 ? 0 : hours,
    minutes: minutes < 0 ? 0 : minutes,
    seconds: seconds < 0 ? 0 : seconds
  })
}

export function isSameOrAfter(date1: string, date2: string, unit: OpUnitType = 'date') {
  return dayjs(date1).isSameOrAfter(date2, unit)
}

export function isSameOrBefore(date1: string, date2: string, unit: OpUnitType = 'date') {
  return dayjs(date1).isSameOrBefore(date2, unit)
}

export function isBefore(date1: string, date2: string, unit: OpUnitType = 'date') {
  return dayjs(date1).isBefore(date2, unit)
}

export function isAfter(date1: string, date2: string, unit: OpUnitType = 'date') {
  return dayjs(date1).isAfter(date2, unit)
}

export function isSame(date1: string, date2: string, unit: OpUnitType = 'date') {
  return dayjs(date1).isSame(date2, unit)
}

export function formatDayJs(date: string, format: string) {
  return dayjs(date).format(format)
}

const ALTERNATIVE_FORMAT = 'EXT_'
export function alternativeDayjsFormat(date: dayjs.ConfigType, format: string): string {
  return dayjs(date)
    .format(ALTERNATIVE_FORMAT + format)
    .slice(ALTERNATIVE_FORMAT.length)
}

const FULL_DAY_FORMATS: Record<TInternalLanguage, string> = {
  en: 'ddd, D MMM YYYY',
  lv: 'ddd, YYYY. [g.] D. MMM',
  ru: 'ddd, D MMM YYYY [г.]',
  de: 'ddd, D. MMM YYYY',
  et: 'ddd, D. MMM YYYY',
  lt: 'ddd, YYYY [m.] MMM D [d.]',
  fi: 'ddd, D. MMM YYYY',
  uk: 'ddd, D MMM YYYY [р.]'
}

const CAPITALIZED_MONTH_LOCALES = new Set(['en', 'de'])

export function formatFullDayDate(date: dayjs.ConfigType, locale: string): string {
  const safeLocale: TInternalLanguage = isInternalLanguage(locale) ? locale : 'en'
  const format = FULL_DAY_FORMATS[safeLocale]
  const instance = dayjs(date).locale(safeLocale)
  const formattedDate = alternativeDayjsFormat(instance, format)

  if (CAPITALIZED_MONTH_LOCALES.has(safeLocale)) {
    return formattedDate
  }

  const month = alternativeDayjsFormat(instance, 'MMM')

  return formattedDate.replace(month, month.toLowerCase())
}
